import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { 
    LANGUAGES,
    DEFAULT_LANG,
    STORAGE_KEY,
    getLanguage,
    setLanguage as i18nSetLanguage,
    getText as i18nGetText,
    loadNamespaces,
    reloadTranslations as i18nReloadTranslations,
} from '../../lib/i18n';

// Re-export for backward compatibility
export const supportedLanguages = LANGUAGES;
export const defaultLanguage = DEFAULT_LANG;
export const LANGUAGE_STORAGE_KEY = STORAGE_KEY;

// Legacy language names format
export const languageNames = LANGUAGES.reduce((acc, lang) => {
    acc[lang.code] = lang.name;
    return acc;
}, {});

// Create context
const I18nContext = createContext(null);

/**
 * I18nProvider - 提供多語言功能
 * 
 * 簡化設計：
 * 1. t(key) - 使用 i18next 翻譯 (需要對應的 namespace)
 * 2. getText(obj) - 從多語言物件直接取值 (用於 templates 中的內嵌翻譯)
 * 3. 頁面載入時自動載入對應的 namespace
 * 4. 語言檔載入完成前顯示載入中狀態
 */
export function I18nProvider({ children }) {
    const { t: i18nextT, i18n: i18nInstance, ready } = useTranslation('common');

    // 切換語言
    const setLanguage = useCallback(async (langCode) => {
        await i18nSetLanguage(langCode);
    }, []);

    // 翻譯函數 - 用於翻譯 key
    const t = useCallback((key, options = {}) => {
        return i18nextT(key, options);
    }, [i18nextT]);

    // 取得本地化文字 - 用於多語言物件 { en: '...', 'zh-TW': '...' }
    const getText = useCallback((textObj, fallback = '') => {
        return i18nGetText(textObj, fallback);
    }, []);

    // 載入 namespace
    const loadNS = useCallback(async (namespaces) => {
        await loadNamespaces(namespaces);
    }, []);

    // 重新載入翻譯
    const reloadTranslations = useCallback(async () => {
        await i18nReloadTranslations();
    }, []);

    const value = {
        // 語言相關
        language: i18nInstance.language || getLanguage(),
        setLanguage,
        setLang: setLanguage, // 別名
        languages: LANGUAGES, // 給 LanguageSwitcher 使用
        
        // 翻譯函數
        t,
        getText,
        getLocalizedText: getText, // 向後相容
        
        // 載入函數
        loadNamespaces: loadNS,
        loadFormTranslations: async () => null, // 向後相容，不再需要
        reloadTranslations,
        
        // 狀態
        supportedLanguages: LANGUAGES.map(l => l.code),
        languageNames,
        isLoading: !ready,
        error: null,
        i18n: i18nInstance,
        ready,
    };

    // 語言檔載入完成前顯示載入中狀態
    if (!ready) {
        return (
            <I18nContext.Provider value={value}>
                <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        <span className="text-gray-500">Loading...</span>
                    </div>
                </div>
            </I18nContext.Provider>
        );
    }

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
}

/**
 * useI18n Hook
 * 
 * @param {string|string[]} namespaces - 需要載入的 namespace (可選)
 * @returns {object} i18n context
 */
export function useI18n(namespaces) {
    const context = useContext(I18nContext);
    const { t, i18n: i18nInstance, ready } = useTranslation(namespaces || 'common');
    
    // 如果有指定 namespace，載入它
    useEffect(() => {
        if (namespaces) {
            const ns = Array.isArray(namespaces) ? namespaces : [namespaces];
            loadNamespaces(ns);
        }
    }, [namespaces]);
    
    if (!context) {
        // Fallback: If not within provider, use react-i18next directly
        return {
            language: i18nInstance.language || getLanguage(),
            setLanguage: i18nSetLanguage,
            setLang: i18nSetLanguage,
            languages: LANGUAGES,
            t,
            getText: (textObj, fb = '') => i18nGetText(textObj, fb),
            getLocalizedText: (textObj, ns = 'common', fb = '') => i18nGetText(textObj, fb),
            loadNamespaces,
            loadFormTranslations: async () => null,
            reloadTranslations: i18nReloadTranslations,
            supportedLanguages: LANGUAGES.map(l => l.code),
            languageNames,
            isLoading: !ready,
            error: null,
            i18n: i18nInstance,
            ready,
        };
    }
    
    // 如果有指定 namespace，覆蓋 t 函數
    if (namespaces) {
        return {
            ...context,
            t,
            ready,
            isLoading: !ready,
        };
    }
    
    return context;
}

/**
 * useText Hook - 簡化版，專門處理多語言物件
 * 
 * 用於 templates 中的多語言物件，例如：
 * { en: 'Name', 'zh-TW': '姓名', 'zh-CN': '姓名' }
 */
export function useText() {
    const { language } = useI18n();
    
    return useCallback((textObj, fallback = '') => {
        return i18nGetText(textObj, fallback);
    }, [language]);
}

// Standalone function for use outside React components
export function getLocalizedText(textObj, namespace = 'common', fallback = '') {
    return i18nGetText(textObj, fallback);
}

// Standalone getText
export { i18nGetText as getText };

// Get current language (standalone)
export { getLanguage as getCurrentLanguage };

// Change language (standalone)
export const changeLanguage = i18nSetLanguage;

// Load form translations (standalone) - deprecated
export const loadFormTranslations = async () => null;

export default I18nContext;
